import {
  createPaymentDropinOrder,
  createPaymentDropinOrderDetail,
  getPaymentChannelMethodList,
} from '@web-publish-id/payment-shared/apis/payment'
import { AdyenPaymentMethodType } from '../types/adyen'
import { AdyenCheckout, Card, GooglePay, WeChat } from '@adyen/adyen-web/auto'
import { toIETFLangCode } from '@web-publish-id/shared'
import { PAYMENT_APP_VERSION } from '../const'
import { PaymentOrderInfo, PublishPaymentMethodItem } from '../apis/types/payment'
import { isPaymentRetryError } from '../utils/paymentRetry'

const baseConfiguration = {
  environment: import.meta.env.VITE_ADYEN_ENVIRONMENT || ('test' as const),
}

// 支付方式列表
const paymentMethodsResponse = {
  data: null,
  _fetch: null,
}

// 获取支付方式列表
const fetchPaymentMethods = () => {
  if (paymentMethodsResponse.data) {
    return paymentMethodsResponse.data
  }
  if (paymentMethodsResponse._fetch) {
    return paymentMethodsResponse._fetch
  }
  paymentMethodsResponse._fetch = getPaymentChannelMethodList({
    payment_channel_type: 1,
  }).then((res) => {
    return (paymentMethodsResponse.data = res.channel_payment_methods)
  })

  return paymentMethodsResponse._fetch
}

/**
 * 获取币种倍率
 * @param {*} currency 币种
 * @returns
 */
const getMultiple = (currency: string) => {
  let multiple
  switch (currency) {
    case 'BHD':
    case 'IQD':
    case 'JOD':
    case 'KWD':
    case 'LYD':
    case 'OMR':
    case 'TND':
      multiple = 1000
      break
    case 'CVE':
    case 'DJF':
    case 'GNF':
    case 'IDR':
    case 'JPY':
    case 'KMF':
    case 'KRW':
    case 'PYG':
    case 'RWF':
    case 'UGX':
    case 'VND':
    case 'VUV':
    case 'XAF':
    case 'XOF':
    case 'XPF':
      multiple = 1
      break
    default:
      multiple = 100
  }
  return multiple
}

// 获取语言码
const getLocale = (locale) => {
  // 特殊处理阿拉伯语言
  if (locale === 'ar') return locale
  return toIETFLangCode(locale)
}

export const createAdyenDropIn = (config: {
  clientKey: string
  locale: string
  sdkVersion?: string
  orderInfo: PaymentOrderInfo

  onSubmit?: (state: any) => void
  onSubmitResult?: (result: any) => void
  onCompleted?: () => void
  onFailed?: (error?: any) => void
  onError?: (error: any) => void
  onLoading?: (loading: boolean) => void
  onCreateOrder?: (result: any) => void
  on3DS2Challenge?: () => void
  getPaymentId?: () => string
}) => {
  let dropin: Card | GooglePay | WeChat
  const { clientKey, locale, sdkVersion = PAYMENT_APP_VERSION, orderInfo } = config
  const { pay_type: businessType } = orderInfo

  // 卡信息
  const cardInfo = {
    endDigits: '',
  }

  // 请求错误
  let requestError

  const configuration: any = {
    ...baseConfiguration,
    locale: getLocale(locale),
    clientKey,
    amount: transformAdyenAmount(orderInfo.price, orderInfo.currency),
    countryCode: orderInfo.country_code || navigator.language,

    paymentMethodsResponse: { paymentMethods: [], storedPaymentMethods: [] },

    onAdditionalDetails: async (detailState, component, actions) => {
      try {
        // Make a POST /payments/details request from your server.

        config.onLoading?.(true)
        const result = await createPaymentDropinOrderDetail({
          detail_data: JSON.stringify(detailState.data.details),
          payment_channel_type: 1,
          sys_order_id: orderInfo?.sys_order_id,
          payment_id: config.getPaymentId?.() || undefined,
        }).finally(() => {
          config.onLoading?.(false)
        })

        // If the /payments/details request from your server fails, or if an unexpected error occurs.
        if (!result?.detail_result) {
          actions.reject()
          return
        }
        // const { resultCode, action, order, donationToken } = res

        // If the /payments/details request request from your server is successful, you must call this to resolve whichever of the listed objects are available.
        // You must call this, even if the result of the payment is unsuccessful.
        actions.resolve(result.detail_result)
      } catch (error) {
        requestError = error
        actions.reject()
      }
    },

    onActionHandled: (actionHandled) => {
      // 3DS2挑战
      if (actionHandled?.componentType === '3DS2Challenge') {
        config.on3DS2Challenge?.()
        return
      }
    },

    onPaymentCompleted: (result, component) => {
      if (isPaymentRetryError(requestError)) return
      if (result?.resultCode === 'Authorised') {
        config.onCompleted()
      } else {
        config.onFailed(requestError)
      }
    },

    onPaymentFailed: (result, component) => {
      if (isPaymentRetryError(requestError)) return
      config.onFailed(requestError)
    },
    onError: (error, component) => {
      console.error('onError', error)
      // config.onError?.(error)
    },
  }

  const setupFrom = async (containerId: string, paymentMethod: PublishPaymentMethodItem, _configuration: any = {}) => {
    const { channel_pay_id: type } = paymentMethod
    const allPaymentMethods = await fetchPaymentMethods()
    Object.assign(configuration, _configuration)
    console.log('configuration :>> ', configuration)

    const findPaymentMethod = allPaymentMethods.find((item) => item.type === type)

    configuration.paymentMethodsResponse.paymentMethods = [findPaymentMethod]

    // 设置onSubmit
    configuration.onSubmit = async (state, component, actions) => {
      requestError = undefined
      try {
        console.log('onSubmit :>> ', state)
        // Make a POST /payments request from your server.
        config.onSubmit?.(state)

        // 是否保存卡信息
        const storePaymentMethod = businessType === 2 || state.data?.storePaymentMethod || false

        const result = await createPaymentDropinOrder({
          dropin_channel_data: JSON.stringify(state),
          payment_channel_type: 1,
          payment_method_id: paymentMethod!.payment_method_id,
          sys_order_id: orderInfo?.sys_order_id,
          sdk_version: sdkVersion,
          os: 1,
          save_card_info: storePaymentMethod ? 1 : 2,
          card_number: storePaymentMethod ? cardInfo.endDigits : undefined,
        })

        config.onCreateOrder?.(result)

        config.onSubmitResult?.(result)

        // If the /payments request from your server fails, or if an unexpected error occurs.
        if (!result.result_data) {
          actions.reject()
          return
        }

        // const { resultCode, action, order, donationToken } = result

        // If the /payments request request form your server is successful, you must call this to resolve whichever of the listed objects are available.
        // You must call this, even if the result of the payment is unsuccessful.
        // actions.resolve({ action: result.action_data })
        actions.resolve(result.result_data)
      } catch (error) {
        console.error('onSubmit', error)
        requestError = error
        actions.reject()
        // reject 不保证触发支付失败回调，RETRY 必须显式通知宿主。
        if (isPaymentRetryError(error)) config.onFailed?.(error)
      }
    }

    const checkout = await AdyenCheckout(configuration)

    switch (type) {
      case AdyenPaymentMethodType.SCHEME:
        const isBillingAddressRequired = ['GB', 'US', 'CA'].includes(orderInfo?.country_code || '')

        dropin = new Card(checkout, {
          hasHolderName: true,
          holderNameRequired: true,
          billingAddressRequired: isBillingAddressRequired,
          billingAddressMode: isBillingAddressRequired ? 'partial' : 'none', // 必须是full模式才能显示邮箱
          enableStoreDetails: businessType === 1, // 将显示一个复选框，供您的购物者选择存储其卡详细信息。
          challengeWindowSize: '05',
          onConfigSuccess: (event) => {
            console.log('onConfigSuccess', event)
          },

          onFieldValid: (event) => {
            const { valid, endDigits } = event
            if (!valid) return

            // 设置卡信息
            if (endDigits) {
              cardInfo.endDigits = endDigits
            }
          },
        })

        break

      case AdyenPaymentMethodType.GOOGLE_PAY:
      case AdyenPaymentMethodType.PAY_WITH_GOOGLE:
        dropin = new GooglePay(checkout)
        await dropin.isAvailable()

        break

      case AdyenPaymentMethodType.WECHATPAY_WEB:
      case AdyenPaymentMethodType.WECHATPAY_QR:
        dropin = new WeChat(checkout)

        break

      default:
        throw new Error('Unsupported payment method')
    }

    return dropin.mount(containerId)
  }

  // 从action创建支付方式
  const setupFromAction = async (containerId: string, action: any, config: any = {}) => {
    const checkout = await AdyenCheckout({ ...configuration, ...config })
    return checkout.createFromAction(action).mount(containerId)
  }

  return {
    setupFrom,
    setupFromAction,
  }
}

/**
 * 转换金额
 * @param price 金额
 * @param currency 币种
 * @returns
 */
export const transformAdyenAmount = (price: number, currency: string) => {
  const multiple = getMultiple(currency)
  return {
    // 向上取整
    value: Math.ceil(price * multiple),
    currency,
  }
}

// 判断是否需要3DS2挑战
export const isNeedAdyen3DS2Challenge = (result_data: any) => {
  return !!result_data.action
}
